const RED = [200, 0, 0];
const GREEN = [0, 150, 0];
const PENETRATION_RED = [196, 0, 0];

// Record the meaning of each marked occurrence before PDF wrapping. The same
// word can be part of both "påverkan" and "ingen påverkan" in a single cell.
export function parsePdfComment(value) {
  const raw = String(value ?? '');
  const ranges = [];
  const marker = /~~(IMPACT|NOIMPACT)~~([\s\S]*?)~~\1END~~/g;
  let text = '', last = 0, match;
  while ((match = marker.exec(raw))) {
    text += raw.slice(last, match.index);
    const start = text.length;
    text += match[2];
    if (text.length > start) ranges.push({ start, end: text.length,
      kind: match[1] === 'NOIMPACT' ? 'noImpact' : 'impact' });
    last = marker.lastIndex;
  }
  text += raw.slice(last);
  for (const phrase of ['Objektet penetrerar ytan', 'Object penetrates the surface']) {
    let start = text.indexOf(phrase);
    while (start !== -1) {
      const end = start + phrase.length;
      if (!ranges.some(range => start < range.end && end > range.start)) {
        ranges.push({ start, end, kind: 'penetration' });
      }
      start = text.indexOf(phrase, end);
    }
  }
  ranges.sort((a, b) => a.start - b.start);

  // jsPDF can discard wrapping spaces and normalise tabs/newlines. Track only
  // non-whitespace characters when matching its page-specific text fragments.
  let compactText = '', rangeIndex = 0;
  const compactKinds = [];
  for (let i = 0; i < text.length; i++) {
    while (rangeIndex < ranges.length && ranges[rangeIndex].end <= i) rangeIndex++;
    if (/\s/.test(text[i])) continue;
    compactText += text[i];
    const range = ranges[rangeIndex];
    compactKinds.push(range && range.start <= i ? range.kind : 'normal');
  }
  return { text, ranges, compactText, compactKinds, hasMarkers: last > 0 };
}

export function stylePdfCommentLines(model, lines, offset = 0) {
  const styledLines = [];
  for (const line of lines) {
    const compactLine = line.replace(/\s/g, '');
    const start = compactLine ? model.compactText.indexOf(compactLine, offset) : offset;
    if (start < 0) {
      // Unknown text must not inherit a red colour from another occurrence.
      styledLines.push([{ text: line, kind: 'normal' }]);
      continue;
    }
    const kinds = model.compactKinds.slice(start, start + compactLine.length);
    const runs = [];
    let index = 0;
    for (let i = 0; i < line.length; i++) {
      const whitespace = /\s/.test(line[i]);
      const kind = whitespace
        ? index > 0 && kinds[index - 1] === kinds[index] ? kinds[index] : 'normal'
        : kinds[index++] || 'normal';
      if (runs.length && runs[runs.length - 1].kind === kind) runs[runs.length - 1].text += line[i];
      else runs.push({ text: line[i], kind });
    }
    styledLines.push(runs);
    offset = start + compactLine.length;
  }
  return { lines: styledLines, nextOffset: offset };
}

// AutoTable reuses row.raw for continuation rows. Keep the character cursor
// there rather than searching every new line for every impact word again.
export function createPdfCommentHooks(doc) {
  const states = new WeakMap();
  const isComment = data => data.section === 'body' && data.column.index === 4;
  const rowKey = data => data.row.raw && typeof data.row.raw === 'object' ? data.row.raw : data.row;
  return {
    didParseCell(data) {
      if (!isComment(data)) return;
      const model = parsePdfComment(typeof data.cell.raw === 'string' ? data.cell.raw : '');
      if (!model.ranges.length && !model.hasMarkers) return;
      states.set(rowKey(data), { model, offset: 0 });
      const style = data.cell.styles;
      doc.setFont(style.font || 'helvetica', style.fontStyle || 'normal');
      doc.setFontSize(style.fontSize || 9);
      const width = typeof style.cellWidth === 'number' ? style.cellWidth : 80;
      const padding = data.cell.padding('left') + data.cell.padding('right');
      data.cell.text = doc.splitTextToSize(model.text, width - padding);
    },
    willDrawCell(data) {
      if (!isComment(data) || !states.has(rowKey(data))) return;
      data.cell._pdfCommentLines = [...data.cell.text];
      data.cell.text = [];
    },
    didDrawCell(data) {
      if (!isComment(data)) return;
      const state = states.get(rowKey(data));
      if (!state) return;
      const pageLines = data.cell._pdfCommentLines || [];
      const styled = stylePdfCommentLines(state.model, pageLines, state.offset);
      state.offset = styled.nextOffset;
      const style = data.cell.styles;
      const font = style.font || 'helvetica', fontStyle = style.fontStyle || 'normal';
      doc.setFont(font, fontStyle);
      doc.setFontSize(style.fontSize);
      const lineHeight = doc.getLineHeight() / doc.internal.scaleFactor;
      const x = data.cell.x + data.cell.padding('left');
      let y = data.cell.y + data.cell.padding('top');
      for (const runs of styled.lines) {
        let currentX = x;
        for (const run of runs) {
          const color = run.kind === 'noImpact' ? GREEN : run.kind === 'impact' ? RED
            : run.kind === 'penetration' ? PENETRATION_RED : [0, 0, 0];
          doc.setFont(font, run.kind === 'normal' ? fontStyle : 'bold');
          doc.setTextColor(...color);
          doc.text(run.text, currentX, y);
          currentX += doc.getTextWidth(run.text);
        }
        y += lineHeight;
      }
      doc.setFont(font, fontStyle);
      doc.setTextColor(0, 0, 0);
    },
  };
}
